// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useCallback } from "react";
import {
  generateId,
  optimizeSVG,
  pathToPolygon,
  LAYER_CONFIG,
} from "../utils/scenarioEditorUtils";

// Axis-aligned bounds of a parsed editor element (rotation ignored — good
// enough for a fit-to-view box).
const elementBounds = (el) => {
  if (el.type === "rect") {
    return {
      minX: el.x,
      minY: el.y,
      maxX: el.x + (el.width || 0),
      maxY: el.y + (el.height || 0),
    };
  }
  if (el.type === "circle") {
    const r = el.r || 0;
    return {
      minX: el.cx - r,
      minY: el.cy - r,
      maxX: el.cx + r,
      maxY: el.cy + r,
    };
  }
  if (el.type === "polygon" && el.points && el.points.length) {
    const xs = el.points.map((p) => p.x);
    const ys = el.points.map((p) => p.y);
    return {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    };
  }
  if (el.type === "text") {
    const fs = el.fontSize || 14;
    return { minX: el.x, minY: el.y - fs, maxX: el.x, maxY: el.y };
  }
  return null;
};

// Apply a uniform scale + translate (the fit transform) to an element's geometry.
const applyFitTransform = (el, s, ox, oy) => {
  if (el.type === "rect") {
    return {
      ...el,
      x: el.x * s + ox,
      y: el.y * s + oy,
      width: (el.width || 0) * s,
      height: (el.height || 0) * s,
    };
  }
  if (el.type === "circle") {
    return {
      ...el,
      cx: el.cx * s + ox,
      cy: el.cy * s + oy,
      r: (el.r || 0) * s,
    };
  }
  if (el.type === "polygon") {
    return {
      ...el,
      points: (el.points || []).map((p) => ({
        x: p.x * s + ox,
        y: p.y * s + oy,
      })),
    };
  }
  if (el.type === "text") {
    return {
      ...el,
      x: el.x * s + ox,
      y: el.y * s + oy,
      fontSize: (el.fontSize || 14) * s,
    };
  }
  return el;
};

export function useSVGImportExport({
  svgRef,
  scenarioMetadata,
  referencePoints,
  elements,
  currentLayer,
  nextElementId,
  setElements,
  setSelectedElements,
  setNextElementId,
  saveToHistory,
  selectedGroupId,
  setShowPasteSVGDialog,
  setPasteSVGCode,
}) {
  // Build a clean, serialized SVG string from the current canvas: drops UI
  // overlays (grid, handles), converts polygons to paths for storage, and
  // stamps scenario metadata onto the root element. Used by export, clipboard
  // copy, and the "Szenario speichern" backend save path.
  const getSerializedSVG = useCallback(() => {
    if (!svgRef.current) return null;
    const svgElement = svgRef.current.cloneNode(true);
    svgElement
      .querySelectorAll(
        ".grid, .selection-indicator, .resize-handle, .point-handle, .vertex-handle",
      )
      .forEach((el) => el.remove());

    const polygons = svgElement.querySelectorAll("polygon");
    polygons.forEach((poly) => {
      const coordSystem =
        poly.getAttribute("data-coordinate-system") ||
        scenarioMetadata.coordinateSystem ||
        "svg";
      const points = [];
      for (let i = 0; i < poly.points.numberOfItems; i++) {
        const pt = poly.points.getItem(i);
        points.push(`${pt.x},${pt.y}`);
      }
      const pathData = `M ${points.join(" L ")} Z`;
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("d", pathData);
      path.setAttribute("fill", poly.getAttribute("fill") || "#90caf9");
      path.setAttribute("stroke", poly.getAttribute("stroke") || "#1565c0");
      path.setAttribute(
        "stroke-width",
        poly.getAttribute("stroke-width") || "2",
      );
      path.setAttribute("data-coordinate-system", coordSystem);
      if (poly.id) path.setAttribute("id", poly.id);
      if (poly.getAttribute("data-polygon-id")) {
        path.setAttribute(
          "data-polygon-id",
          poly.getAttribute("data-polygon-id"),
        );
      }
      if (poly.parentNode) {
        poly.parentNode.replaceChild(path, poly);
      }
    });

    const { x, y, width, height } = scenarioMetadata.viewBox;
    svgElement.setAttribute("viewBox", `${x} ${y} ${width} ${height}`);
    svgElement.setAttribute("width", width);
    svgElement.setAttribute("height", height);
    svgElement.setAttribute("data-scenario-name", scenarioMetadata.name);
    svgElement.setAttribute(
      "data-scenario-description",
      scenarioMetadata.description,
    );
    svgElement.setAttribute(
      "data-coordinate-system",
      scenarioMetadata.coordinateSystem,
    );
    // Only stamp a real, usable scale. Guards against Infinity/NaN that an
    // earlier divide-by-zero in the GPS scale calc could have produced.
    if (Number.isFinite(scenarioMetadata.scale) && scenarioMetadata.scale > 0) {
      svgElement.setAttribute("data-scale", scenarioMetadata.scale.toString());
    }
    // Background-only zoom factor (see renderBackground); persisted so the
    // editor and any svg_content consumer reproduce the same framing.
    const bgZoom = Number(scenarioMetadata.backgroundZoom);
    if (Number.isFinite(bgZoom) && bgZoom > 0 && bgZoom !== 1) {
      svgElement.setAttribute("data-background-zoom", bgZoom.toString());
    }
    if (referencePoints.length > 0) {
      const refData = referencePoints
        .map((refId) => {
          const pin = elements.find((el) => el.id === refId);
          return pin
            ? {
                id: refId,
                label: pin.label,
                x: pin.x,
                y: pin.y,
                gps: pin.gps,
              }
            : null;
        })
        .filter(Boolean);
      svgElement.setAttribute("data-gps-references", JSON.stringify(refData));
    }
    const svgString = new XMLSerializer().serializeToString(svgElement);
    return optimizeSVG(svgString);
  }, [svgRef, scenarioMetadata, referencePoints, elements]);

  const exportSVG = useCallback(() => {
    if (!svgRef.current) return;
    try {
      const optimizedSVG = getSerializedSVG();
      if (!optimizedSVG) return;
      const blob = new Blob([optimizedSVG], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${scenarioMetadata.name
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase()}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to export SVG:", error);
    }
  }, [svgRef, scenarioMetadata.name, getSerializedSVG]);

  const copySVGToClipboard = useCallback(async () => {
    if (!svgRef.current) return;
    try {
      const optimizedSVG = getSerializedSVG();
      if (!optimizedSVG) return;
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(optimizedSVG);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = optimizedSVG;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      const button = document.getElementById("copy-svg-btn");
      if (button) {
        const originalHTML = button.innerHTML;
        const originalClass = button.className;
        button.innerHTML =
          '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>';
        button.className = originalClass.replace(
          "text-gray-600",
          "text-green-600",
        );
        button.title = "SVG Copied!";
        setTimeout(() => {
          button.innerHTML = originalHTML;
          button.className = originalClass;
          button.title = "Copy SVG to Clipboard";
        }, 2000);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to copy SVG to clipboard:", error);
      const button = document.getElementById("copy-svg-btn");
      if (button) {
        const originalHTML = button.innerHTML;
        const originalClass = button.className;
        button.innerHTML =
          '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>';
        button.className = originalClass.replace(
          "text-gray-600",
          "text-red-600",
        );
        button.title = "Copy Failed!";
        setTimeout(() => {
          button.innerHTML = originalHTML;
          button.className = originalClass;
          button.title = "Copy SVG to Clipboard";
        }, 2000);
      }
    }
  }, [svgRef, getSerializedSVG]);

  const importSVGCode = useCallback(
    (svgCode, options = {}) => {
      const { fitToView = false } = options;
      if (!currentLayer) {
        // eslint-disable-next-line no-alert
        alert("Please select an active layer before pasting SVG elements.");
        return;
      }
      try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = svgCode;
        const svgElement = tempDiv.querySelector("svg");
        if (!svgElement) {
          throw new Error("No SVG element found in the provided code");
        }
        let newElements = [];
        let elementCount = 0;

        // SVG transforms (translate/scale/rotate/matrix on <g> or any node) must
        // be honored, otherwise grouped exports (Illustrator/Figma/Inkscape) land
        // at their raw local coords — usually piled at the origin, far outside the
        // background's view — so the objects exist but appear "lost". We thread a
        // cumulative matrix down the tree and bake it into each shape's geometry.
        const parseTransform = (transformStr) => {
          let m = new DOMMatrix();
          if (!transformStr) return m;
          const re = /(\w+)\s*\(([^)]*)\)/g;
          let match;
          while ((match = re.exec(transformStr)) !== null) {
            const name = match[1];
            const a = match[2]
              .trim()
              .split(/[\s,]+/)
              .map(parseFloat)
              .filter((n) => !Number.isNaN(n));
            switch (name) {
              case "translate":
                m = m.translate(a[0] || 0, a[1] || 0);
                break;
              case "scale":
                m = m.scale(a[0] ?? 1, a[1] ?? a[0] ?? 1);
                break;
              case "rotate":
                m =
                  a.length >= 3
                    ? m
                        .translate(a[1], a[2])
                        .rotate(a[0] || 0)
                        .translate(-a[1], -a[2])
                    : m.rotate(a[0] || 0);
                break;
              case "matrix":
                if (a.length === 6) m = m.multiply(new DOMMatrix(a));
                break;
              case "skewX":
                m = m.skewX(a[0] || 0);
                break;
              case "skewY":
                m = m.skewY(a[0] || 0);
                break;
              default:
                break;
            }
          }
          return m;
        };
        const tp = (m, x, y) => ({
          x: m.a * x + m.c * y + m.e,
          y: m.b * x + m.d * y + m.f,
        });
        const decompose = (m) => ({
          scaleX: Math.hypot(m.a, m.b) || 1,
          scaleY: Math.hypot(m.c, m.d) || 1,
          rotation: (Math.atan2(m.b, m.a) * 180) / Math.PI,
        });

        const parseElement = (element, parentMatrix) => {
          // Compose this node's own transform onto the inherited one.
          const matrix = parentMatrix.multiply(
            parseTransform(element.getAttribute("transform")),
          );
          const { scaleX, scaleY, rotation } = decompose(matrix);
          const avgScale = (scaleX + scaleY) / 2;
          switch (element.tagName.toLowerCase()) {
            case "rect": {
              const p = tp(
                matrix,
                parseFloat(element.getAttribute("x") || 0),
                parseFloat(element.getAttribute("y") || 0),
              );
              const rect = {
                id: generateId(),
                type: "rect",
                layer: currentLayer,
                x: p.x,
                y: p.y,
                width:
                  parseFloat(element.getAttribute("width") || 100) * scaleX,
                height:
                  parseFloat(element.getAttribute("height") || 50) * scaleY,
                fill: element.getAttribute("fill") || "#e3f2fd",
                stroke: element.getAttribute("stroke") || "#1976d2",
                strokeWidth: parseFloat(
                  element.getAttribute("stroke-width") || 2,
                ),
                rotation,
                locked: false,
                visible: true,
                label: `Imported Rect ${nextElementId + elementCount}`,
              };
              newElements.push(rect);
              elementCount++;
              break;
            }
            case "circle": {
              const c = tp(
                matrix,
                parseFloat(element.getAttribute("cx") || 0),
                parseFloat(element.getAttribute("cy") || 0),
              );
              const circle = {
                id: generateId(),
                type: "circle",
                layer: currentLayer,
                cx: c.x,
                cy: c.y,
                r: parseFloat(element.getAttribute("r") || 30) * avgScale,
                fill: element.getAttribute("fill") || "#f3e5f5",
                stroke: element.getAttribute("stroke") || "#7b1fa2",
                strokeWidth: parseFloat(
                  element.getAttribute("stroke-width") || 2,
                ),
                rotation: 0,
                locked: false,
                visible: true,
                label: `Imported Circle ${nextElementId + elementCount}`,
              };
              newElements.push(circle);
              elementCount++;
              break;
            }
            case "ellipse": {
              const rx = parseFloat(element.getAttribute("rx") || 30);
              const ry = parseFloat(element.getAttribute("ry") || 30);
              const c = tp(
                matrix,
                parseFloat(element.getAttribute("cx") || 0),
                parseFloat(element.getAttribute("cy") || 0),
              );
              const ellipse = {
                id: generateId(),
                type: "circle",
                layer: currentLayer,
                cx: c.x,
                cy: c.y,
                r: ((rx + ry) / 2) * avgScale,
                fill: element.getAttribute("fill") || "#f3e5f5",
                stroke: element.getAttribute("stroke") || "#7b1fa2",
                strokeWidth: parseFloat(
                  element.getAttribute("stroke-width") || 2,
                ),
                rotation: 0,
                locked: false,
                visible: true,
                label: `Imported Ellipse ${nextElementId + elementCount}`,
              };
              newElements.push(ellipse);
              elementCount++;
              break;
            }
            case "polygon":
            case "polyline": {
              const pointsStr = element.getAttribute("points") || "";
              const points = pointsStr
                .trim()
                .split(/[\s,]+/)
                .reduce((acc, val, index, arr) => {
                  if (index % 2 === 0 && index + 1 < arr.length) {
                    acc.push(
                      tp(matrix, parseFloat(val), parseFloat(arr[index + 1])),
                    );
                  }
                  return acc;
                }, []);
              if (points.length >= 3) {
                const polygon = {
                  id: generateId(),
                  type: "polygon",
                  layer: currentLayer,
                  groupId: selectedGroupId || null,
                  points: points,
                  fill: element.getAttribute("fill") || "#fff3e0",
                  stroke: element.getAttribute("stroke") || "#f57c00",
                  strokeWidth: parseFloat(
                    element.getAttribute("stroke-width") || 2,
                  ),
                  rotation: 0,
                  locked: false,
                  visible: true,
                  label: `Imported ${element.tagName} ${nextElementId + elementCount}`,
                };
                newElements.push(polygon);
                elementCount++;
              }
              break;
            }
            case "text": {
              const textContent =
                element.textContent ||
                element.getAttribute("data-text") ||
                `Imported Text ${nextElementId + elementCount}`;
              const p = tp(
                matrix,
                parseFloat(element.getAttribute("x") || 0),
                parseFloat(element.getAttribute("y") || 0),
              );
              const text = {
                id: generateId(),
                type: "text",
                layer: currentLayer,
                x: p.x,
                y: p.y,
                text: textContent,
                fontSize:
                  parseFloat(element.getAttribute("font-size") || 14) * scaleY,
                fill: element.getAttribute("fill") || "#333",
                fontFamily:
                  element.getAttribute("font-family") || "Arial, sans-serif",
                rotation,
                locked: false,
                visible: true,
              };
              newElements.push(text);
              elementCount++;
              break;
            }
            case "line": {
              const p1 = tp(
                matrix,
                parseFloat(element.getAttribute("x1") || 0),
                parseFloat(element.getAttribute("y1") || 0),
              );
              const p2 = tp(
                matrix,
                parseFloat(element.getAttribute("x2") || 100),
                parseFloat(element.getAttribute("y2") || 0),
              );
              const length = Math.sqrt(
                Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2),
              );
              const angle =
                (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
              const line = {
                id: generateId(),
                type: "rect",
                layer: currentLayer,
                x: Math.min(p1.x, p2.x),
                y: Math.min(p1.y, p2.y) - 1,
                width: length,
                height:
                  parseFloat(element.getAttribute("stroke-width") || 2) *
                  avgScale,
                fill: element.getAttribute("stroke") || "#333",
                stroke: element.getAttribute("stroke") || "#333",
                strokeWidth: 0,
                rotation: angle,
                locked: false,
                visible: true,
                label: `Imported Line ${nextElementId + elementCount}`,
              };
              newElements.push(line);
              elementCount++;
              break;
            }
            case "path": {
              const pathData = element.getAttribute("d") || "";
              const coordSystem =
                element.getAttribute("data-coordinate-system") ||
                scenarioMetadata.coordinateSystem ||
                "svg";
              const polygonPoints = pathToPolygon(pathData, coordSystem).map(
                (pt) => tp(matrix, pt.x, pt.y),
              );

              if (polygonPoints.length >= 3) {
                const polygon = {
                  id: generateId(),
                  type: "polygon",
                  layer: currentLayer,
                  groupId: selectedGroupId || null,
                  points: polygonPoints,
                  fill: element.getAttribute("fill") || "#90caf9",
                  stroke: element.getAttribute("stroke") || "#1565c0",
                  strokeWidth: parseFloat(
                    element.getAttribute("stroke-width") || 2,
                  ),
                  rotation: 0,
                  locked: false,
                  visible: true,
                  label:
                    element.getAttribute("id") ||
                    `Imported Polygon ${nextElementId + elementCount}`,
                  polygonId:
                    element.getAttribute("data-polygon-id") ||
                    element.getAttribute("id") ||
                    null,
                  coordinateSystem: coordSystem,
                };
                newElements.push(polygon);
                elementCount++;
              } else {
                const pathBounds = element.getBBox
                  ? element.getBBox()
                  : { x: 0, y: 0, width: 100, height: 50 };
                const p = tp(
                  matrix,
                  pathBounds.x + pathBounds.width / 2,
                  pathBounds.y + pathBounds.height / 2,
                );
                const path = {
                  id: generateId(),
                  type: "text",
                  layer: currentLayer,
                  x: p.x,
                  y: p.y,
                  text: "Path Element (manual conversion needed)",
                  fontSize: 12,
                  fill: "#ff5722",
                  fontFamily: "Arial, sans-serif",
                  rotation: 0,
                  locked: false,
                  visible: true,
                };
                newElements.push(path);
                elementCount++;
              }
              break;
            }
            case "g":
            case "svg":
              Array.from(element.children).forEach((child) => {
                parseElement(child, matrix);
              });
              break;
            default:
              // eslint-disable-next-line no-console
              console.log(`Unsupported SVG element: ${element.tagName}`);
              break;
          }
        };
        Array.from(svgElement.children).forEach((child) => {
          parseElement(child, new DOMMatrix());
        });
        if (newElements.length === 0) {
          throw new Error("No supported SVG elements found to import");
        }

        // Fit-to-canvas: scale + center the whole imported group into the
        // scenario viewBox (preserving aspect ratio, ~10% margin) so it lands
        // on top of the background instead of wherever the source SVG's own
        // coordinate space happened to place it.
        if (fitToView) {
          let minX = Infinity;
          let minY = Infinity;
          let maxX = -Infinity;
          let maxY = -Infinity;
          newElements.forEach((el) => {
            const b = elementBounds(el);
            if (!b) return;
            minX = Math.min(minX, b.minX);
            minY = Math.min(minY, b.minY);
            maxX = Math.max(maxX, b.maxX);
            maxY = Math.max(maxY, b.maxY);
          });
          const bw = maxX - minX;
          const bh = maxY - minY;
          const {
            x: vbX,
            y: vbY,
            width: vbW,
            height: vbH,
          } = scenarioMetadata.viewBox;
          if (bw > 0 && bh > 0 && vbW > 0 && vbH > 0) {
            const margin = 0.9; // leave a 10% border around the content
            const s = Math.min((vbW * margin) / bw, (vbH * margin) / bh);
            const ox = vbX + (vbW - bw * s) / 2 - minX * s;
            const oy = vbY + (vbH - bh * s) / 2 - minY * s;
            newElements = newElements.map((el) =>
              applyFitTransform(el, s, ox, oy),
            );
          }
        }

        saveToHistory();
        setElements((prev) => [...prev, ...newElements]);
        setSelectedElements(newElements);
        setNextElementId((prev) => prev + elementCount);
        if (setShowPasteSVGDialog) setShowPasteSVGDialog(false);
        if (setPasteSVGCode) setPasteSVGCode("");
        // eslint-disable-next-line no-alert
        alert(
          `Successfully imported ${newElements.length} elements to the ${LAYER_CONFIG[currentLayer].name} layer!`,
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to parse SVG code:", error);
        // eslint-disable-next-line no-alert
        alert(
          `Failed to parse SVG code: ${error.message}\n\nPlease check that the SVG code is valid and contains supported elements (rect, circle, polygon, text, line).`,
        );
      }
    },
    [
      currentLayer,
      nextElementId,
      saveToHistory,
      setElements,
      setSelectedElements,
      setNextElementId,
      scenarioMetadata,
      selectedGroupId,
      setShowPasteSVGDialog,
      setPasteSVGCode,
    ],
  );

  return {
    exportSVG,
    copySVGToClipboard,
    importSVGCode,
    getSerializedSVG,
  };
}
